import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { finalize, share, tap } from 'rxjs/operators';

interface CachedResponse<T> {
  response: HttpResponse<T>;
  expires?: number; // Timestamp when the cache expires (only for TTL cache)
}

@Injectable()
export class ApiCacheInterceptor implements HttpInterceptor {
  // Cache for endpoints with TTL
  private ttlCache = new Map<string, CachedResponse<any>>();
  // Simple cache for endpoints without TTL
  private simpleCache = new Map<string, HttpResponse<any>>();

  // Store ongoing requests to prevent duplicate calls
  private cacheObservable = new Map<string, Observable<any>>();

  // Endpoints that should clear all caches when called
  private endpointsToRefresh = new Set([
    '/services/user/user/set_primary_facility',
  ]);

  // Endpoints that should be cached without TTL
  private endpointsToCache = new Set([
    '/services/user/user/me',
    '/services/user/ms_client',
    '/services/user/ms_client/feature_flags',
    '/services/user/direct_blocks/client',
  ]);

  // Endpoints that should be cached with TTL (10 seconds by default)
  private endpointsWithTtl = new Set(['/services/user/facility']);

  defaultTtl = 3 * 1000;

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const url = req.url.replace(environment.api_url_v1, '');
    const refresh = req.params.get('refresh');

    // Clear caches if URL is in refresh list or refresh param is set
    if (this.endpointsToRefresh.has(url) || refresh) {
      this.clearCaches();
    }

    // Check if URL is eligible for caching
    const isTtlCached = this.endpointsWithTtl.has(url);
    const isSimpleCached = this.endpointsToCache.has(url);

    // Clear caches on modifying methods for both TTL and non-TTL caches
    if (isSimpleCached && ['POST', 'PUT'].includes(req.method)) {
      this.clearCaches();
    }

    if ((isTtlCached || isSimpleCached) && req.method === 'GET') {
      const cacheKey = req.urlWithParams; // Include query parameters in cache key
      const now = Date.now();

      // Check TTL cache first
      if (isTtlCached) {
        const cached = this.ttlCache.get(cacheKey);
        if (cached) {
          if (!cached.expires || cached.expires > now) {
            return of(cached.response.clone());
          }
          // Remove expired entry
          this.ttlCache.delete(cacheKey);
        }
      }
      // Then check simple cache
      else if (isSimpleCached && this.simpleCache.has(cacheKey)) {
        return of(this.simpleCache.get(cacheKey)!.clone());
      }

      // Check for ongoing request
      const cachedObservable = this.cacheObservable.get(cacheKey);
      if (cachedObservable) {
        return cachedObservable;
      }

      // Create new request and cache it
      const observable = next.handle(req).pipe(
        tap(event => {
          if (event instanceof HttpResponse) {
            if (isTtlCached) {
              this.ttlCache.set(cacheKey, {
                response: event.clone(),
                expires: now + this.defaultTtl,
              });
            } else if (isSimpleCached) {
              this.simpleCache.set(cacheKey, event.clone());
            }
          }
        }),
        share(),
        finalize(() => this.cacheObservable.delete(cacheKey)),
      );

      this.cacheObservable.set(cacheKey, observable);
      return observable;
    }

    return next.handle(req);
  }

  /**
   * Clear all caches
   */
  public clearCaches(): void {
    this.simpleCache.clear();
    this.ttlCache.clear();
    this.cacheObservable.clear();
  }

  /**
   * Add an endpoint to be cached with TTL
   * @param endpoint The endpoint URL (without base URL)
   * @param ttl Time to live in milliseconds (default: 10000)
   */
  public addTtlEndpoint(endpoint: string): void {
    this.endpointsWithTtl.add(endpoint);
  }

  /**
   * Remove an endpoint from TTL cache
   * @param endpoint The endpoint URL to remove
   */
  public removeTtlEndpoint(endpoint: string): void {
    this.endpointsWithTtl.delete(endpoint);
    // Clear any existing cache for this endpoint
    Array.from(this.ttlCache.keys())
      .filter(key => key.includes(endpoint))
      .forEach(key => this.ttlCache.delete(key));
  }

  /**
   * Add an endpoint to simple cache (no TTL)
   * @param endpoint The endpoint URL (without base URL)
   */
  public addSimpleCacheEndpoint(endpoint: string): void {
    this.endpointsToCache.add(endpoint);
  }

  /**
   * Remove an endpoint from simple cache
   * @param endpoint The endpoint URL to remove
   */
  public removeSimpleCacheEndpoint(endpoint: string): void {
    this.endpointsToCache.delete(endpoint);
    // Clear any existing cache for this endpoint
    Array.from(this.simpleCache.keys())
      .filter(key => key.includes(endpoint))
      .forEach(key => this.simpleCache.delete(key));
  }
}
